/**
 * Notification System Component
 * 
 * WMS DAOUXUN notification system with:
 * - Real-time notifications
 * - Multiple notification types (info, warning, error, success)
 * - Notification marking as read
 * - Notification refresh
 * - Badge count display
 * 
 * Uses React Query hooks for notification data operations.
 * 
 * @component
 * @returns {JSX.Element} The notification system component
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  MarkEmailRead as MarkReadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { api } from "@/services/api";
import { API_ENDPOINTS } from '@/config/api.config.js';

// Notification types
const NOTIFICATION_TYPES = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success'
};
// Icons by type
const NOTIFICATION_ICONS = {
  [NOTIFICATION_TYPES.INFO]: InfoIcon,
  [NOTIFICATION_TYPES.WARNING]: WarningIcon,
  [NOTIFICATION_TYPES.ERROR]: ErrorIcon,
  [NOTIFICATION_TYPES.SUCCESS]: SuccessIcon
};
const NotificationSystem = () => {
  const { i18n, t } = useTranslation();
  // States
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, categories: {} });
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load notifications - NO FALLBACK
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`${API_ENDPOINTS.NOTIFICATIONS}`);
      const data = response?.data || {};
      const list = Array.isArray(data.notifications) ? data.notifications : Array.isArray(data) ? data : [];
      setNotifications(list);
      setStats({
        total: list.length,
        unread: list.filter(n => !n.read).length,
        categories: {}
      });
    } catch (err) {
      setError(t('notifications.errors.loadError', { message: err.response?.data?.message || err.message }));
      setNotifications([]);
      setStats({ total: 0, unread: 0, categories: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notifications on mount
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Periodic notification refresh
  useEffect(() => {
    const interval = setInterval(() => {
      loadNotifications();
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Open/close popover
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Reload notifications on open
    if (!anchorEl) {
      loadNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mark a notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`${API_ENDPOINTS.NOTIFICATIONS}/${notificationId}/read`);
      
      // Update locally
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, read: true }
            : notif
        )
      );
      
      // Update stats
      setStats(prev => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1)
      }));
      
    } catch (err) {
      setError(t('notifications.errors.markReadError', 'Error marking as read: {{message}}', { message: err.response?.data?.message || err.message }));
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await api.post(`${API_ENDPOINTS.NOTIFICATIONS}/mark-all-read`);
      
      // Update locally
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      
      // Update stats
      setStats(prev => ({ ...prev, unread: 0 }));
      
    } catch (err) {
      setError(t('notifications.errors.markAllReadError', 'Error marking all as read: {{message}}', { message: err.response?.data?.message || err.message }));
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(i18n.language || 'en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return t('common.invalidDate', 'Invalid date');
    }
  };

  // Get chip color based on priority
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Notification button with badge */}
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={t('notifications.ariaLabel')}
      >
        <Badge 
          badgeContent={error ? '!' : stats.unread} 
          color={error ? 'error' : 'secondary'}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      {/* Notification popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 400, maxHeight: 500 }
        }}
      >
        <Box p={2}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{t('notifications.title')}</Typography>
            <Box>
              <IconButton 
                size="small" 
                onClick={loadNotifications}
                disabled={loading}
                title={t('common.refresh', 'Refresh')}
              >
                <RefreshIcon />
              </IconButton>
              {stats.unread > 0 && (
                <Button 
                  size="small" 
                  onClick={markAllAsRead}
                  startIcon={<MarkReadIcon />}
                  sx={{ ml: 1 }}
                >
                  {t('notifications.markAllRead')}
          </Button>
              )}
            </Box>
          </Box>

          {/* Statistics */}
          {!error && (
            <Box display="flex" gap={1} mb={2}>
              <Chip 
                label={t('notifications.stats.total', { count: stats.total })} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={t('notifications.stats.unread', { count: stats.unread })} 
                size="small" 
                color="primary" 
              />
              {Object.entries(stats.categories || {}).map(([category, count]) => (
                <Chip 
                  key={category}
                  label={t('notifications.stats.category', { category, count })} 
                  size="small" 
                  variant="outlined" 
                />
              ))}
            </Box>
          )}

          {/* Loading state */}
          {loading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>{t('notifications.empty')}</Typography>
            </Box>
          )}

          {/* API Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {error}
              </Typography>
              <Button 
                size="small" 
                onClick={loadNotifications}
                startIcon={<RefreshIcon />}
                sx={{ mt: 1 }}
              >
                {t('common.retry', 'Retry')}
              </Button>
            </Alert>
          )}

          {/* Notification list or empty state */}
          {!loading && !error && (
            <>
              {notifications.length === 0 ? (
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  py={4}
                  color="text.secondary"
                >
                  <NotificationsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body2">
                    {t('notifications.noNotifications', 'No notifications available')}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {notifications.map((notification, index) => {
                    const IconComponent = NOTIFICATION_ICONS[notification.type] || InfoIcon;
                    
                    return (
                      <React.Fragment key={notification.id}>
                        <ListItem
                          sx={{
                            bgcolor: notification.read ? 'transparent' : 'action.hover',
                            borderRadius: 1,
                            mb: 1
                          }}
                        >
                          <ListItemIcon>
                            <IconComponent 
                              color={notification.type === 'error' ? 'error' : 
                                     notification.type === 'warning' ? 'warning' :
                                     notification.type === 'success' ? 'success' : 'info'} 
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2">
                                  {notification.title}
                                </Typography>
                                <Box display="flex" gap={0.5}>
                                  <Chip 
                                    label={notification.priority || 'medium'} 
                                    size="small" 
                                    color={getPriorityColor(notification.priority)}
                                  />
                                  {!notification.read && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => markAsRead(notification.id)}
                                      title={t('notifications.markAsRead', 'Mark as read')}
                                    >
                                      <MarkReadIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {notification.message}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(notification.timestamp)} • {notification.category}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < notifications.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </>
          )}

          {/* Footer */}
          {!error && notifications.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box textAlign="center">
                <Button size="small" variant="text">
                  {t('notifications.viewAll', 'View all notifications')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(NotificationSystem);

